import { Component, Input, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";

/** Angular error key -> i18n key. Keys all exist in assets/i18n/en.json. */
const ERROR_TEXT_KEYS: { [errorKey: string]: string } = {
  max: "MAX_INPUT",
  maxlength: "MAX_LENGTH_INPUT",
  min: "MIN_INPUT",
  minlength: "MIN_LENGTH_INPUT",
  required: "REQUIRED_INPUT",
};

@Component({
  selector: "checkbox-group-with-input",
  templateUrl: "checkbox-group-with-input.component.html",
})
export class CheckboxGroupWithInputComponent implements OnInit {
  // Inputs
  // REQUIRED
  @Input() formGroup: FormGroup;
  @Input() checkboxControlName: string;
  @Input() checkboxGroupLabel: string;
  @Input() options: Array<{ value: string; label: string }>;
  @Input() isReadOnly: boolean = false;
  // OPTIONAL: Other Input
  @Input() otherControlName?: string;
  @Input() otherLabel?: string = "other";
  @Input() otherPlaceholder?: string = "other";
  @Input() otherMaxLength?: number = 100;
  // OPTIONAL: Comment Text Area
  @Input() commentControlName?: string;
  @Input() commentLabel?: string;
  @Input() commentMaxLength?: number = 6000;
  @Input() commentRows?: number = 4;

  // Instance Variables
  private showCommentTextArea: boolean = false;
  private showOtherInput: boolean = false;

  ngOnInit(): void {
    // Initialize the checkbox control with an empty array if it doesn't have a value
    const checkboxControl = this.formGroup.get(this.checkboxControlName);
    if (checkboxControl && !checkboxControl.value) {
      checkboxControl.setValue([]);
    }
    this.showOtherInput = this.shouldShowOtherInput();
    this.showCommentTextArea = this.shouldShowCommentTextArea();
  }

  private shouldShowCommentTextArea(): boolean {
    return !!(this.commentControlName && this.commentLabel);
  }

  private shouldShowOtherInput(): boolean {
    return !!(
      this.otherControlName &&
      this.otherLabel &&
      this.otherPlaceholder
    );
  }

  /**
   * Checks if a specific checkbox option is currently selected
   * @param value - The value of the checkbox option to check
   * @returns true if the option is selected
   */
  isChecked(value: string): boolean {
    const control = this.formGroup.get(this.checkboxControlName);
    if (!control || !control.value) {
      return false;
    }
    const selectedValues = control.value as string[];
    return selectedValues.includes(value);
  }

  /**
   * Handles checkbox change events
   * Adds or removes the value from the array of selected values
   * @param value - The value of the checkbox that was toggled
   * @param event - The checkbox change event
   */
  onCheckboxChange(value: string, event: any): void {
    const control = this.formGroup.get(this.checkboxControlName);
    if (!control) {
      return;
    }

    const selectedValues = (control.value as string[]) || [];
    const isChecked = event.detail.checked;

    if (isChecked) {
      // Add value if not already present
      if (!selectedValues.includes(value)) {
        control.setValue([...selectedValues, value]);
      }
    } else {
      // Remove value
      control.setValue(selectedValues.filter((v) => v !== value));
    }

    // Mark control as touched and dirty
    control.markAsTouched();
    control.markAsDirty();
  }

  /**
   * Renders unconditionally on invalidity (no touched/dirty gate) — a value
   * loaded from the server can be invalid before the user touches anything,
   * which is exactly how SUP-20043 produced a dead Save button with no reason
   * shown. Mirrors date-time-field, whose showValidation defaults to true.
   */
  hasVisibleError(controlName: string): boolean {
    const control = this.formGroup && this.formGroup.get(controlName);
    return !!control && control.invalid && !control.disabled;
  }

  errorTextFor(controlName: string): string {
    const control = this.formGroup && this.formGroup.get(controlName);
    if (!control || !control.errors) {
      return "";
    }
    const keys = Object.keys(control.errors);
    for (let i = 0; i < keys.length; i++) {
      if (ERROR_TEXT_KEYS[keys[i]]) {
        return ERROR_TEXT_KEYS[keys[i]];
      }
    }
    // An unmapped validator still gets a truthful message rather than silence:
    // saying nothing is what made SUP-20043 undiagnosable in the first place.
    return "error";
  }
}



// WEBPACK FOOTER //
// ./src/components/checkbox-group-with-input/checkbox-group-with-input.component.ts